import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePriceLists, useUploadPriceList, type PriceLists } from "../../api/admin";
import { formatDate } from "../../api/dates";
import { Card } from "../../components/ui";
import { AdminDrawer } from "./AdminDrawer";
import AdminLayout from "./AdminLayout";
import { PriceListDetailView, PriceListStatus } from "./PriceListDetail";

/** Supplier price lists: every version, which is in force, upload and
 * activate. An upload prices nothing until it is put in force, so a list can
 * be checked before a single quotation reads from it. */
export function PriceListsPage() {
  const { id } = useParams();
  const selected = id === undefined ? null : Number(id);
  const view = usePriceLists();
  const [uploading, setUploading] = useState(false);
  const data = view.data;
  return (
    <AdminLayout
      title="Price lists"
      sub="One version in force per supplier"
      actions={
        <button type="button" className="btn btn-primary" disabled={!data} onClick={() => setUploading(true)}>
          + Upload a price list
        </button>
      }
    >
      {view.isError ? <p className="notice bad">The price lists could not be loaded: {view.error.message}</p> : null}
      {view.isPending ? <div className="table-card empty-state">Loading price lists…</div> : null}
      {data && data.rows.length === 0 ? (
        <div className="table-card empty-state">
          <strong>No suppliers with a price list.</strong> Upload a supplier's list to start one.
        </div>
      ) : null}
      {data && data.rows.length > 0 ? <VersionsTable data={data} selected={selected} /> : null}
      {data ? <KitsInForceCard data={data} /> : null}
      {selected !== null ? <PriceListDetailView id={selected} /> : null}
      {uploading && data ? (
        <AdminDrawer title="Upload a price list" onClose={() => setUploading(false)}>
          <UploadPriceList data={data} onReview={() => setUploading(false)} />
        </AdminDrawer>
      ) : null}
    </AdminLayout>
  );
}

/** Each supplier's service kits in force, by size, linking to the version that
 * carries them. Read-only, like the kits themselves. */
export function KitsInForceCard({ data }: { data: PriceLists }) {
  const withKits = data.rows.filter((row) => row.active_id !== null && row.kits_in_force.length > 0);
  if (withKits.length === 0) {
    return null;
  }
  return (
    <Card title="Service kits in force" sub="Read from each supplier's workbook; they change when a new version is put in force">
      <ul className="history-list">
        {withKits.map((row) => (
          <li key={row.supplier.id}>
            <Link className="go-link" to={`/admin/price-lists/${row.active_id}`}>
              {row.supplier.name}
            </Link>
            : {row.kits_in_force.map((kit) => kit.size_label).join(", ")}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function VersionsTable({ data, selected }: { data: PriceLists; selected: number | null }) {
  return (
    <div className="table-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>Supplier</th>
            <th>Version</th>
            <th>File</th>
            <th>Sheet</th>
            <th className="num">Parts</th>
            <th>Uploaded</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.flatMap((row) =>
            row.versions.length === 0
              ? [
                  <tr key={`none-${row.supplier.id}`}>
                    <td>{row.supplier.name}</td>
                    <td colSpan={6} className="muted">
                      No list uploaded yet.
                    </td>
                  </tr>,
                ]
              : row.versions.map((v, index) => (
                  <tr key={v.id} className={v.id === selected ? "selected" : undefined}>
                    <td>{index === 0 ? row.supplier.name : ""}</td>
                    <td>
                      <Link className="go-link" to={`/admin/price-lists/${v.id}`}>
                        v{v.version_no}
                      </Link>
                    </td>
                    <td>{v.filename}</td>
                    <td>{v.sheet}</td>
                    <td className="num">
                      {v.rows_loaded}
                      {v.rows_skipped ? <span className="sub">+{v.rows_skipped} skipped</span> : null}
                    </td>
                    <td>{formatDate(v.uploaded_at)}</td>
                    <td>
                      <PriceListStatus status={v.status} />
                    </td>
                  </tr>
                )),
          )}
        </tbody>
      </table>
    </div>
  );
}

function UploadPriceList({ data, onReview }: { data: PriceLists; onReview: () => void }) {
  const upload = useUploadPriceList();
  const [supplier, setSupplier] = useState("");
  const [sheet, setSheet] = useState("");
  const [notes, setNotes] = useState("");
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="admin-form">
      <label className="field">
        Supplier
        <select value={supplier} onChange={(e) => setSupplier(e.target.value)}>
          <option value="">Choose…</option>
          {data.suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        Sheet
        <select value={sheet} onChange={(e) => setSheet(e.target.value)}>
          <option value="">Choose…</option>
          {data.sheets.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        Notes
        <input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <input
        ref={input}
        type="file"
        accept=".xlsx"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            upload.mutate({ supplierId: Number(supplier), sheet, notes, file });
          }
          e.target.value = "";
        }}
      />
      <p className="muted-note">Uploading files the list; it prices nothing until it is put in force. Up to {data.max_upload_mb} MB.</p>
      {upload.isError ? <p className="notice bad">{upload.error.message}</p> : null}
      {upload.isSuccess ? (
        <p className="notice ok">
          Uploaded as v{upload.data.price_list.version_no}.{" "}
          <Link to={`/admin/price-lists/${upload.data.price_list.id}`} onClick={onReview}>
            Review and put it in force
          </Link>
        </p>
      ) : null}
      <div className="admin-form-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={upload.isPending || supplier === "" || sheet === ""}
          onClick={() => input.current?.click()}
        >
          {upload.isPending ? "Reading…" : "Choose the workbook"}
        </button>
      </div>
    </div>
  );
}
